/**
 * Merge a canonical backend Project response into the browser cache without
 * letting its intentionally stripped media arrays erase local-only recovery
 * fields. Persisted server fields still win for every matched photo/document/video,
 * and rows omitted by the server are never resurrected.
 */

type LooseProject = Record<string, any>;

type OwnedIdResolver = (ids: number[], projectId: string) => Promise<Iterable<number>>;

export interface ProjectReadLocalMediaDeps {
    resolveOwnedPhotoIds?: OwnedIdResolver;
    resolveOwnedDocumentIds?: OwnedIdResolver;
    resolveOwnedVideoIds?: OwnedIdResolver;
}

const DEVICE_LOCAL_PROJECT_MEDIA_KEYS = new Set([
    'blobid',
    'docblobid',
    'videoblobid',
    'localblobonly',
]);

const STRIPPED_DEVICE_LOCAL_MEDIA = Symbol('stripped-device-local-media');
const BINARY_CONTAINER_KEYS = new Set([
    'bytes', 'buffer', 'arraybuffer', 'blob', 'file', 'docbytes', 'pdfblob',
]);

function normaliseKey(key: unknown): string {
    return String(key ?? '').replace(/[_-]/g, '').toLowerCase();
}

function isDeviceLocalMediaString(value: unknown): boolean {
    return typeof value === 'string' && (
        /^\s*blob:/i.test(value)
        || /^\s*data:(?:image|video)\/[^,\s"'<>]*,/i.test(value)
    );
}

function isByteArray(value: unknown): value is number[] {
    return Array.isArray(value)
        && value.length > 0
        && value.every(entry => Number.isInteger(entry) && entry >= 0 && entry <= 255);
}

function isPlainRecord(value: unknown): value is LooseProject {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

function isSerialisedBuffer(value: unknown): boolean {
    return isPlainRecord(value) && value.type === 'Buffer' && isByteArray(value.data);
}

function isNumericByteObject(value: unknown): boolean {
    if (!isPlainRecord(value)) return false;
    const entries = Object.entries(value);
    return entries.length > 0 && entries.every(([key, entry]) => (
        /^\d+$/.test(key) && Number.isInteger(entry) && Number(entry) >= 0 && Number(entry) <= 255
    ));
}

const PROJECT_MEDIA_RETIREMENT_TRANSIENT_KEYS = [
    'mediaRetirementReceipts',
    'media_retirement_receipts',
    'mediaRetirementOutcome',
    'media_retirement_outcome',
] as const;

/**
 * Media-retirement receipts are one-save transaction commands and outcomes.
 * They are never Project state, so every cache/baseline caller shares this
 * shallow canonicalisation boundary instead of independently remembering
 * which camel/snake aliases must be stripped.
 */
export function stripProjectMediaRetirementTransientFields(
    project: LooseProject,
): LooseProject {
    const canonical = { ...project };
    for (const key of PROJECT_MEDIA_RETIREMENT_TRANSIENT_KEYS) delete canonical[key];
    return canonical;
}

function stripProjectDeviceLocalMediaRefsInner(value: any, parentKey = ''): any {
    if (isDeviceLocalMediaString(value)) return STRIPPED_DEVICE_LOCAL_MEDIA;
    if (typeof Blob !== 'undefined' && value instanceof Blob) return STRIPPED_DEVICE_LOCAL_MEDIA;
    if (typeof ArrayBuffer !== 'undefined'
        && (value instanceof ArrayBuffer || ArrayBuffer.isView(value))) {
        return STRIPPED_DEVICE_LOCAL_MEDIA;
    }
    if (Array.isArray(value)) {
        if (BINARY_CONTAINER_KEYS.has(normaliseKey(parentKey)) && isByteArray(value)) {
            return STRIPPED_DEVICE_LOCAL_MEDIA;
        }
        return value.flatMap((child) => {
            const clean = stripProjectDeviceLocalMediaRefsInner(child);
            return clean === STRIPPED_DEVICE_LOCAL_MEDIA ? [] : [clean];
        });
    }
    if (!isPlainRecord(value)) return value;
    if (
        isSerialisedBuffer(value)
        || (BINARY_CONTAINER_KEYS.has(normaliseKey(parentKey)) && isNumericByteObject(value))
    ) {
        return STRIPPED_DEVICE_LOCAL_MEDIA;
    }
    const clean: LooseProject = {};
    for (const [key, child] of Object.entries(value)) {
        if (DEVICE_LOCAL_PROJECT_MEDIA_KEYS.has(normaliseKey(key))) continue;
        const portable = stripProjectDeviceLocalMediaRefsInner(child, key);
        if (portable === STRIPPED_DEVICE_LOCAL_MEDIA) continue;
        clean[key] = portable;
    }
    return clean;
}

/** Remove browser-profile-only media from an API Project copy. */
export function stripProjectDeviceLocalMediaRefs(value: any): any {
    const clean = stripProjectDeviceLocalMediaRefsInner(value);
    return clean === STRIPPED_DEVICE_LOCAL_MEDIA ? undefined : clean;
}

function finiteLocalIds(rows: unknown, key: string): number[] {
    if (!Array.isArray(rows)) return [];
    return [...new Set(rows.flatMap((row) => {
        const value = row && typeof row === 'object' ? (row as LooseProject)[key] : null;
        return typeof value === 'number' && Number.isFinite(value) ? [value] : [];
    }))];
}

async function defaultOwnedPhotoIds(ids: number[], projectId: string): Promise<number[]> {
    const { getProjectBlobIds } = await import('./photo-blob-store');
    const owned = new Set<number>((await getProjectBlobIds(projectId)).map((row: any) => row.id));
    return ids.filter(id => owned.has(id));
}

async function defaultOwnedDocumentIds(ids: number[], projectId: string): Promise<number[]> {
    const { inspectDocumentBlobReferences } = await import('./document-blob-store');
    const inspection = await inspectDocumentBlobReferences(ids, projectId);
    return inspection.ownedIds;
}

async function defaultOwnedVideoIds(ids: number[], projectId: string): Promise<number[]> {
    const { inspectVideoBlobReferences } = await import('./video-blob-store');
    const inspection = await inspectVideoBlobReferences(ids, projectId);
    return inspection.ownedIds;
}

async function resolvedOwnedSet(
    resolver: OwnedIdResolver,
    ids: number[],
    projectId: string,
): Promise<Set<number>> {
    if (ids.length === 0) return new Set();
    try {
        const resolved = await resolver(ids, projectId);
        const candidates = new Set(ids);
        return new Set([...resolved].filter(id => candidates.has(id)));
    } catch (error: any) {
        // An unavailable local store cannot establish ownership. Preserve the
        // portable server Project and retry local recovery on a later reopen.
        console.warn('[QM] Browser-local Project media ownership check failed; portable cloud media remains active:', error?.message || error);
        return new Set();
    }
}

function mergeOwnedLocatorRows(
    localRows: unknown,
    serverRows: unknown,
    locatorKey: 'blobId' | 'docBlobId' | 'videoBlobId',
    ownedIds: Set<number>,
): unknown {
    if (!Array.isArray(serverRows) || !Array.isArray(localRows)) return serverRows;
    const localById = new Map(localRows
        .filter(row => row && typeof row === 'object' && row.id != null)
        .map(row => [String(row.id), row]));
    return serverRows.map((serverRow) => {
        if (!serverRow || typeof serverRow !== 'object' || serverRow.id == null) return serverRow;
        const local = localById.get(String(serverRow.id));
        const localId = local && typeof local === 'object' ? local[locatorKey] : null;
        if (typeof localId !== 'number' || !ownedIds.has(localId)) return serverRow;
        return {
            ...serverRow,
            [locatorKey]: localId,
            ...(locatorKey === 'blobId' && local._localBlobOnly === true
                ? { _localBlobOnly: true }
                : {}),
        };
    });
}

/**
 * Rehydrate only owner-proved recovery locators from the exact local Dexie
 * Project after an API GET. The server remains authoritative for rows/content;
 * local rows omitted by the server are never resurrected.
 */
export async function mergeProjectReadResultForDexie(
    localProject: LooseProject | null | undefined,
    serverProject: LooseProject,
    deps: ProjectReadLocalMediaDeps = {},
): Promise<LooseProject> {
    const server = stripProjectDeviceLocalMediaRefs(
        stripProjectMediaRetirementTransientFields(serverProject || {}),
    );
    const serverId = String(server.id || server.dexieId || server.dexie_id || '');
    const localId = String(localProject?.id || localProject?.dexieId || localProject?.dexie_id || '');
    if (!serverId || serverId !== localId) return server;

    const photoIds = finiteLocalIds(localProject?.photos, 'blobId');
    const documentIds = finiteLocalIds(localProject?.documents, 'docBlobId');
    const videoIds = finiteLocalIds(localProject?.videos, 'videoBlobId');
    const [ownedPhotos, ownedDocuments, ownedVideos] = await Promise.all([
        resolvedOwnedSet(deps.resolveOwnedPhotoIds || defaultOwnedPhotoIds, photoIds, serverId),
        resolvedOwnedSet(deps.resolveOwnedDocumentIds || defaultOwnedDocumentIds, documentIds, serverId),
        resolvedOwnedSet(deps.resolveOwnedVideoIds || defaultOwnedVideoIds, videoIds, serverId),
    ]);

    return {
        ...server,
        ...(Object.prototype.hasOwnProperty.call(server, 'photos')
            ? { photos: mergeOwnedLocatorRows(localProject?.photos, server.photos, 'blobId', ownedPhotos) }
            : {}),
        ...(Object.prototype.hasOwnProperty.call(server, 'documents')
            ? { documents: mergeOwnedLocatorRows(localProject?.documents, server.documents, 'docBlobId', ownedDocuments) }
            : {}),
        ...(Object.prototype.hasOwnProperty.call(server, 'videos')
            ? { videos: mergeOwnedLocatorRows(localProject?.videos, server.videos, 'videoBlobId', ownedVideos) }
            : {}),
    };
}

export function mergeProjectSaveResultForDexie(
    requestProject: LooseProject | null | undefined,
    serverProject: LooseProject,
): LooseProject {
    // Synchronous callers cannot prove IndexedDB ownership. The canonical
    // server media arrays therefore win in full. Reattaching request-side
    // bytes, Blob URLs, File objects or integer locators here can bind one
    // browser profile's media to a different Project after an id collision or
    // stale save response. The async owned helper below is the only recovery
    // path and restores locators only after exact Project ownership proof.
    const local = stripProjectDeviceLocalMediaRefs(
        stripProjectMediaRetirementTransientFields(requestProject || {}),
    );
    const server = stripProjectDeviceLocalMediaRefs(
        stripProjectMediaRetirementTransientFields(serverProject),
    );
    const serverId = String(server.id || server.dexieId || server.dexie_id || '');
    const localId = String(local.id || local.dexieId || local.dexie_id || '');
    if (serverId && localId && serverId !== localId) return server;
    const merged = { ...local, ...server };
    if (Object.prototype.hasOwnProperty.call(server, 'photos')) {
        merged.photos = server.photos;
    }
    if (Object.prototype.hasOwnProperty.call(server, 'documents')) {
        merged.documents = server.documents;
    }
    if (Object.prototype.hasOwnProperty.call(server, 'videos')) {
        merged.videos = server.videos;
    }
    return merged;
}

/**
 * Save-result cache merge that restores local integer locators only after the
 * same exact ownership proof used for cloud GET recovery.
 */
export async function mergeProjectSaveResultForDexieOwned(
    requestProject: LooseProject | null | undefined,
    serverProject: LooseProject,
    deps: ProjectReadLocalMediaDeps = {},
): Promise<LooseProject> {
    const locatorFreeMerge = mergeProjectSaveResultForDexie(requestProject, serverProject);
    return mergeProjectReadResultForDexie(requestProject, locatorFreeMerge, deps);
}
